import React, { useState, useEffect, useCallback } from 'react';
import { X, HandThumbsUp, HandThumbsDown } from 'react-bootstrap-icons';
import feedbackService from '../../services/feedbackService';

// Step 1: Thumbs up or down
const INITIAL_OPTIONS = [
  { id: 'yes', label: 'Thumbs up', icon: HandThumbsUp },
  { id: 'not_really', label: 'Thumbs down', icon: HandThumbsDown },
];

// Step 2a: If Yes - favorite feature
const FAVORITE_FEATURE_OPTIONS = [
  { id: 'risk_rules', label: 'Risk rules & limits' },
  { id: 'bracket_orders', label: 'Bracket orders' },
  { id: 'health_dashboard', label: 'Account Health' },
  { id: 'other', label: 'Other', needsTextInput: true },
];

// Step 2b: If Not really - what's stopping you
const NOT_FOR_ME_OPTIONS = [
  { id: 'not_sure', label: "I'm not sure how this works" },
  { id: 'not_convinced', label: "I'm not convinced it will improve my trading" },
  { id: 'safety_concern', label: "You don't support my broker" },
  { id: 'too_expensive', label: "It's too expensive", needsFollowUp: true },
  { id: 'too_restrictive', label: "It seems too restrictive" },
  { id: 'need_proof', label: "I need more proof or results", needsFollowUp: true },
  { id: 'just_browsing', label: "I'm just browsing" },
  { id: 'other', label: 'Other', needsTextInput: true },
];

const buttonLayoutStyle = {
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 16px',
  minWidth: '48px',
};

const FeedbackSlideIn = ({
  isOpen,
  onClose,
  triggerReason,
  primaryQuestion = 'What do you think of PrecisionTrader?',
  primarySubtext = '',
}) => {
  const [step, setStep] = useState('initial'); // 'initial' | 'like_yes' | 'like_no'
  const [selectedOption, setSelectedOption] = useState(null);
  const [otherText, setOtherText] = useState('');
  const [followUpAnswer, setFollowUpAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [pendingFeedback, setPendingFeedback] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setStep('initial');
      setSelectedOption(null);
      setOtherText('');
      setFollowUpAnswer('');
      setShowThankYou(false);
      setPendingFeedback(null);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (pendingFeedback) {
      feedbackService.submitFeedback({
        primaryReason: pendingFeedback,
        triggerReason: triggerReason || 'behavior',
      }).catch((err) => console.error('Error submitting feedback:', err));
      setPendingFeedback(null);
    }
    onClose();
  }, [pendingFeedback, onClose, triggerReason]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) handleClose();
    };
    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  const submitFeedback = async (primaryReason, followUpAnswerText = '') => {
    setPendingFeedback(null);
    setIsSubmitting(true);
    try {
      await feedbackService.submitFeedback({
        primaryReason,
        followUpAnswer: followUpAnswerText || undefined,
        triggerReason: triggerReason || 'behavior',
      });
      setShowThankYou(true);
      setTimeout(() => onClose(), 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setShowThankYou(true);
      setTimeout(() => onClose(), 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInitialSelect = (option) => {
    setPendingFeedback(option.id === 'yes' ? 'Thumbs up' : 'Not really');
    if (option.id === 'yes') {
      setStep('like_yes');
    } else {
      setStep('like_no');
    }
  };

  const handleFavoriteFeatureSelect = (option) => {
    setSelectedOption(option);
    if (!option.needsTextInput) {
      submitFeedback(`Favorite: ${option.label}`);
    }
  };

  const handleFavoriteOtherSubmit = () => {
    if (otherText.trim()) {
      submitFeedback('Favorite: Other', otherText.trim());
    }
  };

  const handleNotForMeSelect = (option) => {
    setSelectedOption(option);
    if (!option.needsFollowUp && !option.needsTextInput) {
      submitFeedback(option.label);
    }
  };

  const handleNotForMeOtherSubmit = () => {
    if (otherText.trim()) {
      submitFeedback('Other', otherText.trim());
    }
  };

  const handleFollowUpSubmit = () => {
    if (followUpAnswer.trim() && selectedOption) {
      const label = selectedOption.label;
      submitFeedback(label, followUpAnswer.trim());
    }
  };

  const selectedNotForMeData = selectedOption
    ? NOT_FOR_ME_OPTIONS.find((opt) => opt.id === selectedOption.id)
    : null;
  const showNotForMeFollowUp = selectedNotForMeData?.needsFollowUp && !showThankYou;
  const showNotForMeOtherInput = selectedNotForMeData?.needsTextInput && !showThankYou;

  if (!isOpen) return null;

  const OptionButton = ({ option, onClick, iconOnly }) => {
    const Icon = option.icon;
    return (
      <button
        type="button"
        onClick={() => onClick(option)}
        className="landing-feedback-option-btn"
        style={iconOnly ? buttonLayoutStyle : undefined}
        title={option.label}
        aria-label={option.label}
      >
        {iconOnly && Icon ? (
          <Icon size={20} color="#e0e0e0" />
        ) : (
          option.label
        )}
      </button>
    );
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    background: 'rgba(255, 255, 255, 0.05)',
    color: '#e0e0e0',
    fontSize: '0.9em',
    marginBottom: '10px',
  };

  const submitButtonStyle = {
    width: '100%',
    color: '#fff',
    border: 'none',
    padding: '10px',
    borderRadius: '8px',
    fontSize: '0.9em',
    fontWeight: '600',
  };

  return (
    <div
      className="feedback-slide-in landing-feedback-panel"
      style={{
        position: 'fixed',
        bottom: '80px',
        right: '20px',
        width: 'auto',
        maxWidth: '280px',
        backgroundColor: 'rgba(30, 30, 40, 0.98)',
        backdropFilter: 'blur(10px)',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
        zIndex: 1001,
        padding: '18px',
        animation: 'slideInRight 0.3s ease-out',
        maxHeight: 'calc(90vh - 100px)',
        overflowY: 'auto',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @media (max-width: 768px) {
          .feedback-slide-in {
            right: 10px; bottom: 80px;
            max-width: 280px;
            padding: 14px;
          }
        }
      `}</style>

      <button
        onClick={handleClose}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          background: 'transparent',
          border: 'none',
          color: '#e0e0e0',
          cursor: 'pointer',
          padding: '4px',
          borderRadius: '50%',
        }}
        aria-label="Close feedback"
      >
        <X size={18} />
      </button>

      {showThankYou && (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <p className="landing-feedback-accent-text" style={{ fontSize: '1.1em', fontWeight: '600', marginBottom: '8px' }}>
            Thank you!
          </p>
          <p style={{ color: '#e0e0e0', fontSize: '0.9em', margin: 0 }}>Your feedback helps us improve.</p>
        </div>
      )}

      {/* Step 1: Do you like it? */}
      {!showThankYou && step === 'initial' && (
        <div>
          <h3 style={{ color: '#fff', fontSize: '1em', fontWeight: '600', marginBottom: primarySubtext ? '6px' : '14px', marginTop: 0, paddingRight: '28px' }}>
            {primaryQuestion}
          </h3>
          {primarySubtext && (
            <p style={{ color: '#e0e0e0', fontSize: '0.85em', marginBottom: '14px', lineHeight: '1.4' }}>
              {primarySubtext}
            </p>
          )}
          <div style={{ display: 'flex', gap: '12px' }}>
            {INITIAL_OPTIONS.map((opt) => (
              <OptionButton key={opt.id} option={opt} onClick={handleInitialSelect} iconOnly />
            ))}
          </div>
        </div>
      )}

      {/* Step 2a: Favorite feature (if Yes) */}
      {!showThankYou && step === 'like_yes' && (
        <div>
          <h3 style={{ color: '#fff', fontSize: '1em', fontWeight: '600', marginBottom: '6px', marginTop: 0, paddingRight: '28px' }}>
            What's your favorite feature?
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {FAVORITE_FEATURE_OPTIONS.map((opt) => (
              <OptionButton key={opt.id} option={opt} onClick={handleFavoriteFeatureSelect} />
            ))}
          </div>
          {selectedOption?.needsTextInput && (
            <div style={{ marginTop: '12px' }}>
              <input
                type="text"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                placeholder="Tell us..."
                className="landing-feedback-input"
                style={{ ...inputStyle, fontFamily: 'inherit' }}
                onKeyPress={(e) => e.key === 'Enter' && otherText.trim() && handleFavoriteOtherSubmit()}
                autoFocus
              />
              <button
                type="button"
                onClick={handleFavoriteOtherSubmit}
                disabled={!otherText.trim() || isSubmitting}
                className="landing-feedback-submit"
                style={{ ...submitButtonStyle, cursor: otherText.trim() && !isSubmitting ? 'pointer' : 'not-allowed' }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 2b: Not for me - what's stopping you */}
      {!showThankYou && step === 'like_no' && !showNotForMeFollowUp && !showNotForMeOtherInput && (
        <div>
          <h3 style={{ color: '#fff', fontSize: '1em', fontWeight: '600', marginBottom: '6px', marginTop: 0, paddingRight: '28px' }}>
            What would help?
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {NOT_FOR_ME_OPTIONS.map((opt) => (
              <OptionButton key={opt.id} option={opt} onClick={handleNotForMeSelect} />
            ))}
          </div>
        </div>
      )}

      {/* Not for me - Other text input */}
      {!showThankYou && step === 'like_no' && showNotForMeOtherInput && (
        <div>
          <h3 style={{ color: '#fff', fontSize: '1em', fontWeight: '600', marginBottom: '8px', marginTop: 0, paddingRight: '28px' }}>
            Tell us more
          </h3>
          <textarea
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder="What would help?"
            rows={3}
            className="landing-feedback-input"
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            autoFocus
          />
          <button
            type="button"
            onClick={handleNotForMeOtherSubmit}
            disabled={!otherText.trim() || isSubmitting}
            className="landing-feedback-submit"
            style={{ ...submitButtonStyle, cursor: otherText.trim() && !isSubmitting ? 'pointer' : 'not-allowed' }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      )}

      {/* Not for me - Price follow-up */}
      {!showThankYou && step === 'like_no' && showNotForMeFollowUp && selectedNotForMeData?.id === 'too_expensive' && (
        <div>
          <h3 style={{ color: '#fff', fontSize: '1em', fontWeight: '600', marginBottom: '8px', marginTop: 0, paddingRight: '28px' }}>
            At what monthly price would this feel like a no-brainer?
          </h3>
          <input
            type="text"
            value={followUpAnswer}
            onChange={(e) => setFollowUpAnswer(e.target.value)}
            placeholder="e.g., $50/month"
            className="landing-feedback-input"
            style={inputStyle}
            onKeyPress={(e) => e.key === 'Enter' && followUpAnswer.trim() && handleFollowUpSubmit()}
            autoFocus
          />
          <button
            type="button"
            onClick={handleFollowUpSubmit}
            disabled={!followUpAnswer.trim() || isSubmitting}
            className="landing-feedback-submit"
            style={{ ...submitButtonStyle, cursor: followUpAnswer.trim() && !isSubmitting ? 'pointer' : 'not-allowed' }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      )}

      {/* Not for me - Proof follow-up */}
      {!showThankYou && step === 'like_no' && showNotForMeFollowUp && selectedNotForMeData?.id === 'need_proof' && (
        <div>
          <h3 style={{ color: '#fff', fontSize: '1em', fontWeight: '600', marginBottom: '8px', marginTop: 0, paddingRight: '28px' }}>
            What would convince you?
          </h3>
          <textarea
            value={followUpAnswer}
            onChange={(e) => setFollowUpAnswer(e.target.value)}
            placeholder="Backtests, live results, demo, etc."
            rows={3}
            className="landing-feedback-input"
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            autoFocus
          />
          <button
            type="button"
            onClick={handleFollowUpSubmit}
            disabled={!followUpAnswer.trim() || isSubmitting}
            className="landing-feedback-submit"
            style={{ ...submitButtonStyle, cursor: followUpAnswer.trim() && !isSubmitting ? 'pointer' : 'not-allowed' }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      )}
    </div>
  );
};

export default FeedbackSlideIn;
